import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  CachedBuildingLabelService,
  DeleteLocalCacheCategory,
  ENVIRONMENT,
  ModalService,
  SharedService,
  TranslateLoader,
  UserService
} from '@cfp/core';
import { ManualEnergyLabelModal, RegisteredEnergyLabelModal } from '@cfp/shared/energy';
import { EnergyLabel, EnergyLabelService } from '@cfp/shared/energy-label';
import { LabelGroupModule, LabelGroupService } from '@cfp/shared/ui/label-group';
import { ContactModal } from '@cfp/shared/ui/modals';
import { WEiiImprovementScale } from '@cfp/shared/weii';
import {
  Alert,
  BreakpointObserver,
  BreakpointState,
  Button,
  Dropdown,
  Icon,
  InfoModal,
  InfoModalButton
} from '@cfp/ui';
import { LoadingSpinner, TranslatedRouterLink } from '@cfp/ui/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BuildingDetailsDTO,
  BuildingEnergyIndexDTO,
  BuildingEnergyIndexesDTO,
  BuildingRegisteredLabelService,
  CountryCodeDTO,
  EnergyLabelSituationTypeDTO,
  EnergyLabelTypeDTO,
  IndexTypeDTO,
  LabelGroupDTO,
  ObjectTypeDTO,
  RegionCodeDTO,
  RegisteredEnergyLabelDTO,
  RegisteredOrCalculatedEnergyLabelDTO
} from 'csr-asset-app';
import { CRREMService } from 'csr-crrem';
import { LicenseDTO } from 'csr-login-app';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { Observable, Subject, catchError, combineLatest, map, merge, of, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'cfp-label-improvement',
  templateUrl: './label-improvement.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LabelGroupModule,
    Dropdown,
    TranslateModule,
    LoadingSpinner,
    EnergyLabel,
    Icon,
    TranslatedRouterLink,
    RouterLink,
    DatePipe,
    Button,
    InfoModalButton,
    BsDropdownModule,
    Alert,
    NgTemplateOutlet,
    WEiiImprovementScale,
    DecimalPipe
  ],
  providers: [DatePipe]
})
export class LabelImprovement {
  protected readonly EnergyLabelSituationTypeDTO = EnergyLabelSituationTypeDTO;
  protected readonly ObjectTypeDTO = ObjectTypeDTO;
  protected readonly LabelGroupDTO = LabelGroupDTO;
  protected readonly IndexTypeDTO = IndexTypeDTO;

  public readonly building = input<BuildingDetailsDTO>();
  public readonly buildingLabelFromInput = input<BuildingEnergyIndexDTO>(undefined, { alias: 'buildingLabel' });
  public readonly labelGroup = model<LabelGroupDTO>();
  public readonly hasIndexRangeLicense = input<boolean>(false);
  public readonly labelGroups = model<LabelGroupDTO[]>();
  public readonly currentLabel = model<RegisteredOrCalculatedEnergyLabelDTO>();
  public readonly titleClass = input<string>();
  public readonly canChangeLabelGroup = input<boolean>(true);

  public readonly datePipe = inject(DatePipe);

  protected readonly improvedLabel = computed(() => {
    return this.buildingLabel()?.improvedLabel;
  });

  protected readonly titleText = computed(() => {
    return this.getTranslatedLabelGroupText('improved.label.title', this.labelGroup());
  });

  protected readonly currentLabelText = computed(() => {
    const currentLabel = this.currentLabel();
    const labelGroup = this.labelGroup();
    if (!currentLabel || !labelGroup) {
      return;
    }
    if (currentLabel.labelType === EnergyLabelTypeDTO.USERDEFINED) {
      return this.getTranslatedLabelGroupText('energy.label.userDefined', labelGroup);
    } else if (currentLabel?.validUntil) {
      return this.getTranslatedLabelGroupText('energy.label.certified', labelGroup);
    } else {
      return this.getTranslatedLabelGroupText('energy.label.calculated', labelGroup);
    }
  });

  protected readonly improvedLabelText = computed(() => {
    const improvedLabel = this.buildingLabel()?.improvedLabel;
    const labelGroup = this.labelGroup();
    if (!improvedLabel || !labelGroup) {
      return;
    }
    if (improvedLabel.labelType === EnergyLabelTypeDTO.USERDEFINED) {
      return this.getTranslatedLabelGroupText('improved.label.userDefined', labelGroup);
    } else {
      return this.getTranslatedLabelGroupText('improved.label', labelGroup);
    }
  });

  protected readonly registeredLabel = computed(() => {
    const currentLabel = this.currentLabel();
    const clarificationLabel = this.clarificationLabel();
    if (currentLabel?.labelType === EnergyLabelTypeDTO.REGISTERED) {
      return currentLabel;
    } else if (clarificationLabel?.labelType === EnergyLabelTypeDTO.REGISTERED) {
      return clarificationLabel;
    }
  });

  protected readonly calculatedLabel = computed(() => {
    const currentLabel = this.currentLabel();
    const clarificationLabel = this.clarificationLabel();
    if (currentLabel?.labelType === EnergyLabelTypeDTO.CALCULATEDCURRENT) {
      return currentLabel;
    } else if (clarificationLabel?.labelType === EnergyLabelTypeDTO.CALCULATEDCURRENT) {
      return clarificationLabel;
    }
  });

  private getTranslatedLabelGroupText(baseKey: string, labelGroup?: LabelGroupDTO): string {
    const interpolateParams = {
      labelGroupRating: this.translateService.instant(`labelGroup.rating.${labelGroup}`)
    };

    if (labelGroup) {
      const key = `${baseKey}.${labelGroup}`;
      const text = this.translateService.instant(key, interpolateParams);
      if (text !== key) {
        return text;
      }
    }
    return this.translateService.instant(baseKey, interpolateParams);
  }

  protected readonly clarificationLabel = computed(() => {
    return this.buildingLabel()?.clarificationLabel;
  });

  protected readonly buildingLabel = computed(() => {
    const buildingLabelFromInput = this.buildingLabelFromInput();
    if (buildingLabelFromInput) {
      return buildingLabelFromInput;
    }

    const labelGroup = this.labelGroup();
    const energyLabels = this.energyLabels();
    if (!labelGroup || !energyLabels) {
      return;
    }

    return energyLabels?.[labelGroup];
  });

  protected readonly clarificationText = computed(() => {
    const currentLabel = this.currentLabel();
    const buildingLabel = this.buildingLabel();
    const clarificationLabel = this.clarificationLabel();
    const clarificationText: string[] = [];
    if (!currentLabel || !clarificationLabel || !buildingLabel) {
      return;
    }

    const registeredLabel = this.registeredLabel();
    if (registeredLabel) {
      clarificationText.push(
        this.translateService.instant(`improved.label.info.certified.${this.labelGroup()}`, {
          label: this.translatedRegisteredLabel(),
          date: this.datePipe.transform(registeredLabel.validUntil, 'longDate')
        })
      );
    }

    if (
      buildingLabel.labelSituationType === EnergyLabelSituationTypeDTO.CALCULATEDLABELISBETTERTHANREGISTERED ||
      buildingLabel.labelSituationType ===
        EnergyLabelSituationTypeDTO.CALCULATEDLABELISBETTERTHANREGISTEREDUSEROPTEDOPTIMIZEDLABEL
    ) {
      clarificationText.push(
        this.translateService.instant('improved.label.calculatedLabelHigher', {
          registeredLabel: this.translatedRegisteredLabel(),
          calculatedLabel: this.translatedCalculatedLabel()
        })
      );
    } else if (buildingLabel.labelSituationType === EnergyLabelSituationTypeDTO.CALCULATEDLABELISWORSETHANREGISTERED) {
      clarificationText.push(
        this.translateService.instant('improved.label.calculatedLabelLower', {
          registeredLabel: this.translatedRegisteredLabel(),
          calculatedLabel: this.translatedCalculatedLabel()
        })
      );
    }

    return clarificationText.join(' ');
  });

  protected readonly canManuallyChangeLabel = computed(() => {
    if (this.environment.components.canManuallyChangeLabel !== true) {
      return false;
    }
    if (!this.userService.hasScope('buildings-energylabel-modify')) {
      return false;
    }
    // Currently we only support dutch EPC labels to be manually changed
    return (
      this.buildingLabel()?.currentLabel.indexType === IndexTypeDTO.EI ||
      this.buildingLabel()?.currentLabel.indexType === IndexTypeDTO.EP2
    );
  });

  protected readonly canManuallyRegisterLabel = computed(() => {
    const buildingLabel = this.buildingLabel();
    if (!buildingLabel) {
      return;
    }

    return this.userService.hasScope('buildings-properties-modify') && buildingLabel?.isManualRegisteredLabelSupported;
  });

  protected readonly isExpired = computed(() => {
    const currentLabelValidUntil = this.buildingLabel()?.currentLabel.validUntil;
    if (!currentLabelValidUntil) {
      return;
    }

    const validUntilDate = new Date(currentLabelValidUntil);
    return validUntilDate && validUntilDate < new Date();
  });

  protected readonly showlabelSituationType = computed(() => {
    return true; // TODO: Should we also show it for NABERS? this.labelGroup() !== LabelGroupDTO.NABERS;
  });

  protected readonly translatedRegisteredLabel = computed(() => {
    const labelGroup = this.labelGroup();
    const registeredLabel = this.registeredLabel();
    if (!labelGroup || !registeredLabel) {
      return;
    }
    return this.energyLabelService.getTranslatedLabel(labelGroup, registeredLabel.label);
  });

  private readonly translatedCalculatedLabel = computed(() => {
    const labelGroup = this.labelGroup();
    const calculatedLabel = this.calculatedLabel();
    if (!labelGroup || !calculatedLabel) {
      return;
    }
    return this.energyLabelService.getTranslatedLabel(labelGroup, calculatedLabel.label);
  });

  protected readonly isCrremTranslationLoaded = toSignal(
    toObservable(this.labelGroup).pipe(
      switchMap((labelGroup) => {
        if (labelGroup !== LabelGroupDTO.CRREM) {
          return of(false);
        }
        return this.translateLoader.loadTranslation('crrem').pipe(map(() => true));
      })
    )
  );

  protected readonly crremLabel = toSignal(
    combineLatest([toObservable(this.building), toObservable(this.labelGroup)]).pipe(
      switchMap(([building, labelGroup]) => {
        if (!building || labelGroup !== LabelGroupDTO.CRREM) {
          return of(undefined);
        }
        return this.crremService.v2BuildingsBuildingIdCrremLabelGet(building.id);
      })
    )
  );

  protected readonly isLoading = signal<boolean>(false);
  protected readonly hasError = signal<boolean>(false);

  protected readonly environment = inject(ENVIRONMENT);
  private readonly cachedBuildingLabelService = inject(CachedBuildingLabelService);
  private readonly translateService = inject(TranslateService);
  private readonly sharedService = inject(SharedService);
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly energyLabelService = inject(EnergyLabelService);
  private readonly crremService = inject(CRREMService);
  private readonly translateLoader = inject(TranslateLoader);
  private readonly buildingRegisteredLabelService = inject(BuildingRegisteredLabelService);

  protected readonly hasEnergyLabelHistoryLicense = toSignal(
    this.userService.hasLicense(LicenseDTO.LicenseTypeEnum.EnergyLabelHistory)
  );

  protected readonly showEnergyLabelHistoryOption = computed(() => {
    const labelSituationType = this.buildingLabel()?.labelSituationType;
    if (!labelSituationType) {
      return false;
    }
    return (
      this.hasEnergyLabelHistoryLicense() &&
      (labelSituationType === EnergyLabelSituationTypeDTO.CALCULATEDLABELISBETTERTHANREGISTERED ||
        labelSituationType ===
          EnergyLabelSituationTypeDTO.CALCULATEDLABELISBETTERTHANREGISTEREDUSEROPTEDOPTIMIZEDLABEL ||
        labelSituationType === EnergyLabelSituationTypeDTO.CALCULATEDLABELISEQUALTOREGISTERED ||
        labelSituationType === EnergyLabelSituationTypeDTO.CALCULATEDLABELISWORSETHANREGISTERED)
    );
  });

  protected readonly showRegisteredLabelForImprovedLabel = computed(() => {
    const hasIndexRangeLicense = this.hasIndexRangeLicense();
    const labelSituationType = this.buildingLabel()?.labelSituationType;
    const showLabelSituationType = this.showlabelSituationType();
    if (!labelSituationType || !hasIndexRangeLicense || !showLabelSituationType) {
      return false;
    }
    return (
      labelSituationType === EnergyLabelSituationTypeDTO.CALCULATEDLABELISBETTERTHANREGISTEREDUSEROPTEDOPTIMIZEDLABEL
    );
  });

  constructor() {
    effect(() => {
      const buildingLabel = this.buildingLabel();
      this.currentLabel.set(buildingLabel?.currentLabel);
    });
  }
  protected showRefineBuildingCharacteristics = this.environment.components.showRefineBuildingCharacteristics;
  protected showMainLabelOnly: boolean = false; // Is used by the label improvent inline component

  protected readonly showLabelGroupsInDropdown = toSignal(
    this.breakpointObserver.observe().pipe(
      map((state: BreakpointState) => {
        return state.breakpoints.xs === true || state.breakpoints.sm === true;
      })
    )
  );

  protected readonly showIndexRangeDropdownOptions = computed(() => {
    const hasIndexRangeLicense = this.hasIndexRangeLicense();
    const isLabelGroupEpc = this.labelGroup() === LabelGroupDTO.EPC;
    return hasIndexRangeLicense && isLabelGroupEpc;
  });

  protected readonly showEnergyLabelDropdownOptions = computed(() => {
    return (
      this.showEnergyLabelHistoryOption() ||
      this.showRegisteredLabelForImprovedLabel() ||
      this.showIndexRangeDropdownOptions()
    );
  });

  private localReloadTrigger = new Subject<void>();
  private reloadTrigger = merge(this.sharedService.reloadComponentObservable, this.localReloadTrigger).pipe(
    map(() => {
      return Date.now();
    })
  );

  protected readonly energyLabels = toSignal(
    combineLatest([toObservable(this.building), this.reloadTrigger.pipe(startWith(null))]).pipe(
      switchMap(([building]) => {
        if (!building) {
          return of(undefined);
        }
        return this.getLabels(building.id);
      })
    )
  );

  protected showManualEnergyLabelModal(): void {
    this.modalService.show(ManualEnergyLabelModal, {
      class: 'modal-sm modal-dialog-centered',
      initialState: {
        buildingId: this.building()?.id,
        labelType: this.buildingLabel()?.currentLabel.labelType,
        indexType: IndexTypeDTO.EP2, // Currently we only support EP2 labels as manual type
        currentLabel: this.buildingLabel()?.currentLabel.label,
        improvedLabel: this.buildingLabel()?.improvedLabel.label
      }
    });
  }

  protected showRegisteredEneryLabelModal(): void {
    const registeredEnergyLabel = this.clarificationLabel()?.registrationDate
      ? this.clarificationLabel()
      : this.currentLabel();
    const modalRef = this.modalService.show(RegisteredEnergyLabelModal, {
      class: 'modal-sm modal-dialog-centered',
      initialState: {
        labelGroup: this.labelGroup(),
        indexType: registeredEnergyLabel?.indexType
      }
    });

    if (modalRef.content) {
      modalRef.content.building.set(this.building());
      modalRef.content.registeredEnergyLabel.set(
        registeredEnergyLabel?.registrationDate ? (registeredEnergyLabel as RegisteredEnergyLabelDTO) : undefined
      );
    }
  }

  protected onLabelGroupChange(labelGroup?: LabelGroupDTO): void {
    const labelGroups = this.labelGroups();
    if (!labelGroups) {
      return;
    }
    const isInLabelGroups = labelGroup && labelGroups.includes(labelGroup);
    const currentLabelGroup = this.labelGroup();
    const isCurrentInLabelGroups = currentLabelGroup ? labelGroups.includes(currentLabelGroup) : false;
    if (isInLabelGroups || !isCurrentInLabelGroups) {
      this.labelGroup.set(labelGroup);
    }
  }

  protected showOldMethodologyModal(): void {
    this.modalService.showSidePanel(InfoModal, {
      initialState: {
        title: 'energylabel.improvement.oldMethodologyExplanationTitle',
        body: 'energylabel.improvement.oldMethodologyExplanationBody'
      }
    });
  }

  protected showContactModal(): void {
    const building = this.building();
    const portfolioId = building?.portfolio?.id;
    const buildingId = building?.id;
    if (!portfolioId || !buildingId) {
      return;
    }
    this.translateService.get('energylabel.improvement.contactSubject').subscribe((subject: string) => {
      this.modalService.show(ContactModal, {
        class: 'modal-dialog-scrollable',
        ignoreBackdropClick: true,
        initialState: {
          showMessage: true,
          showPhone: true,
          subject: subject,
          portfolioId: portfolioId,
          buildingId: buildingId
        }
      });
    });
  }

  protected readonly infoModalBodyText = computed(() => {
    const building = this.building();
    const currentLabel = this.currentLabel();
    if (!building || !building.address || !currentLabel) {
      return;
    }

    if (building.address.countryCode === CountryCodeDTO.GB && currentLabel.indexType === 'EPC') {
      if (building.address.regionCode === RegionCodeDTO.GBSCT) {
        return 'info.energy-label-improvement.body.scotland';
      } else {
        return 'info.energy-label-improvement.body.england';
      }
    } else {
      return `info.energy-label-improvement.body.${currentLabel.indexType}`;
    }
  });

  protected async openIndexRange(): Promise<void> {
    const building = this.building();
    if (!building) {
      return;
    }

    const { IndexRangeSidePanel } = await import('@cfp/shared/energy');
    const modalRef = this.modalService.showSidePanel(IndexRangeSidePanel);
    if (modalRef.content) {
      modalRef.content.buildingId.set(building.id);
    }
  }

  protected async openEnergyLabelHistory(): Promise<void> {
    const building = this.building();
    const labelGroup = this.labelGroup();
    if (!building || !labelGroup) {
      return;
    }

    const { EnergyLabelHistorySidePanel } = await import('@cfp/features-shared/energy-label-history');
    const modalRef = this.modalService.showSidePanel(EnergyLabelHistorySidePanel);
    if (modalRef.content) {
      modalRef.content.buildingId.set(building.id);
      modalRef.content.labelGroup.set(labelGroup);
    }
  }

  private getLabels(buildingId: number): Observable<{ [key: string]: BuildingEnergyIndexDTO } | undefined> {
    this.isLoading.set(true);
    this.hasError.set(false);
    return this.cachedBuildingLabelService.v2BuildingsIdLabelsGet(buildingId).pipe(
      map((buildingLabels: BuildingEnergyIndexesDTO) => {
        this.isLoading.set(false);

        const energyLabels = buildingLabels.energyLabels;
        const labelGroups = this.labelGroupService.getLabelGroups(energyLabels);
        this.labelGroups.set(labelGroups);

        const queryLabelGroup = this.labelGroupService.queryParamLabelGroup();
        if (queryLabelGroup && Object.values(LabelGroupDTO).includes(queryLabelGroup)) {
          this.labelGroups.set([queryLabelGroup] as LabelGroupDTO[]);
          this.labelGroup.set(queryLabelGroup);
        } else if (labelGroups.length === 1) {
          this.labelGroup.set(labelGroups[0]);
        } else if (this.showMainLabelOnly) {
          const mainLabelGroup = this.labelGroupService.getMainLabelGroup(labelGroups);
          this.labelGroups.set(mainLabelGroup ? [mainLabelGroup] : []);
          this.labelGroup.set(mainLabelGroup);
        }

        return energyLabels;
      }),
      catchError(() => {
        this.isLoading.set(false);
        this.hasError.set(true);
        return of(undefined);
      })
    );
  }

  protected useRegisteredLabelForImprovedLabel(boolean: boolean): void {
    const buildingId = this.building()?.id;
    if (!buildingId) {
      return;
    }
    this.isLoading.set(true);
    this.buildingRegisteredLabelService
      .v2BuildingsBuildingIdRegisteredEnergyLabelSetShowRegisteredOptimizedLabelPut(
        {
          showRegisteredOptimizedLabel: boolean
        },
        buildingId
      )
      .subscribe(() => {
        this.sharedService.emitDeleteLocalCache(DeleteLocalCacheCategory.Portfolio);
        this.localReloadTrigger.next();
      });
  }
}
